import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Paper, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Button, CircularProgress, Alert, alpha, useTheme, Divider,
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import { formatDateLong } from '../../utils/dateUtils';
import LeaveSection from './LeaveSection';
import MonthYearPicker, { formatPeriodLabel, getDefaultPeriod } from '../../components/common/MonthYearPicker';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function StatusChip({ status }) {
  const map = {
    Completed:   { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    Invoiced:    { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    'In Progress': { color: 'info',  icon: <AccessTimeIcon fontSize="small" /> },
    Pending:     { color: 'warning', icon: <WarningIcon fontSize="small" /> },
    Cancelled:   { color: 'error',   icon: <DoNotDisturbIcon fontSize="small" /> },
  };
  const norm = (() => {
    const s = (status || '').toLowerCase();
    if (['completed', 'invoiced'].includes(s)) return 'Completed';
    if (['approved', 'in progress', 'in design', 'proofing', 'uploaded'].includes(s)) return 'In Progress';
    if (['cancelled', 'canceled'].includes(s)) return 'Cancelled';
    return 'Pending';
  })();
  const { color, icon } = map[norm] || { color: 'default', icon: null };
  return <Chip size="small" label={norm} color={color} icon={icon} variant="outlined" />;
}

// ─── MetricCard ───────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub, icon, color }) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        border: `1px solid ${alpha(color || theme.palette.primary.main, 0.18)}`,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 40px 0 rgba(0,0,0,0.10)' },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color: color || 'text.primary' }}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {sub}
              </Typography>
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                width: 48, height: 48, borderRadius: 1.5, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(color || theme.palette.primary.main, 0.1),
                color: color || theme.palette.primary.main,
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── SalesDashboard ───────────────────────────────────────────────────────────

export default function SalesDashboard() {
  const { token, user } = useAuth();
  const theme = useTheme();
  const firstName = user?.name?.split(' ')[0] || 'there';

  const [period, setPeriod] = useState(getDefaultPeriod);
  const periodLabel     = formatPeriodLabel(period);
  const periodShort     = period.view === 'year' ? 'this year'  : 'this month';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const periodParams = period.view === 'year'
        ? `?year=${period.year}&view=year`
        : `?year=${period.year}&month=${period.month}`;

      const res = await fetch(`${config.apiUrl}/api/dashboard/sales-workflow-stats${periodParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) setData(json.data);
      else setError('Failed to load dashboard data');
    } catch {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalRevenue = parseFloat(data?.monthlyRevenueInProgress || 0) + parseFloat(data?.monthlyRevenueInvoiced || 0);

  return (
    <Box>
      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 4, pb: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexWrap: 'wrap', gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}
          >
            {greeting()}, {firstName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your sales overview for {periodLabel}
          </Typography>
        </Box>
        <MonthYearPicker value={period} onChange={setPeriod} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* ── Row 1: Key Metrics ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={`My Revenue — ${periodLabel}`}
            value={formatCurrency(totalRevenue)}
            sub="In progress + invoiced"
            color={theme.palette.success.main}
            icon={<MonetizationOnIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={`Invoiced — ${periodLabel}`}
            value={formatCurrency(data?.monthlyRevenueInvoiced || 0)}
            sub="Confirmed income"
            color={theme.palette.primary.main}
            icon={<CheckCircleIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="My Active Orders"
            value={data?.activeAdOrders ?? 0}
            sub="Ad orders in progress"
            color={theme.palette.info.main}
            icon={<AssignmentIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Follow-Ups Due"
            value={data?.followUpsDue ?? 0}
            sub="Enquiries overdue today"
            color={data?.followUpsDue > 0 ? theme.palette.warning.main : theme.palette.success.main}
            icon={<ScheduleIcon />}
          />
        </Grid>
      </Grid>

      {/* ── Row 2: Converted Enquiries ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Converted Enquiries"
            value={data?.convertedEnquiries ?? 0}
            sub={`Ordered ${periodShort}`}
            color={theme.palette.secondary.main}
            icon={<TrendingUpIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="In Progress Revenue"
            value={formatCurrency(data?.monthlyRevenueInProgress || 0)}
            sub="Not yet invoiced"
            color={theme.palette.info.dark}
            icon={<TrendingUpIcon />}
          />
        </Grid>
      </Grid>

      {/* ── Row 3: Recent Orders + Open Leads ── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Recent Orders */}
        <Grid item xs={12} lg={8}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon sx={{ color: 'primary.main' }} /> My Recent Orders
              </Typography>
              <Button component={Link} to="/app/orders" size="small" variant="outlined">
                View All
              </Button>
            </Box>
            {data?.recentOrders?.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Business / School</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.recentOrders.map(order => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            ORD-{order.id}
                          </Typography>
                        </TableCell>
                        <TableCell>{order.businessName}</TableCell>
                        <TableCell fontWeight={500}>
                          {formatCurrency(order.totalPrice)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateLong(order.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={order.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No orders yet.</Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Open Leads / Enquiries */}
        <Grid item xs={12} lg={4}>
          <Card
            elevation={0}
            sx={{
              height: '100%', borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.22)}`,
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon sx={{ color: 'warning.main' }} /> Open Leads
              </Typography>
              <Button component={Link} to="/app/enquiries" size="small" variant="outlined" color="warning">
                View All
              </Button>
            </Box>
            {data?.openEnquiries?.length > 0 ? (
              <Box sx={{ p: 1 }}>
                {data.openEnquiries.map((enq, i) => (
                  <Box key={enq.id}>
                    {i > 0 && <Divider />}
                    <Box sx={{ px: 1.5, py: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {enq.businessName}
                          </Typography>
                          {enq.contactName?.trim() && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {enq.contactName.trim()}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          size="small"
                          label={enq.outcome === 'Follow Up Later' ? 'Follow Up' : 'New'}
                          color={enq.outcome === 'Follow Up Later' ? 'warning' : 'info'}
                          variant="outlined"
                          sx={{ ml: 1, flexShrink: 0 }}
                        />
                      </Box>
                      {enq.followUpDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <ScheduleIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" color={new Date(enq.followUpDate) <= new Date() ? 'error' : 'text.secondary'}>
                            {new Date(enq.followUpDate) <= new Date() ? 'Overdue: ' : 'Due: '}
                            {new Date(enq.followUpDate).toLocaleDateString('en-AU')}
                          </Typography>
                        </Box>
                      )}
                      {enq.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <PhoneIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">{enq.phone}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No open leads.</Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* ── Leave ── */}
      <LeaveSection />
    </Box>
  );
}
